import React, { useState, useEffect } from 'react';
import config from '../../config';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  useTheme,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PrintIcon from '@mui/icons-material/Print';
import EditIcon from '@mui/icons-material/Edit';
import ReceiptIcon from '@mui/icons-material/Receipt';
import { useLanguage } from '../../contexts/LanguageContext';
import { PurchaseInvoice } from '../../types/purchaseInvoices.types';
import { companyInfoService, CompanyInfo } from '../../services/companyInfo.service';

interface PurchaseInvoicePreviewDialogProps {
  open: boolean;
  onClose: () => void;
  invoice: PurchaseInvoice | null;
  onEdit?: () => void;
}

const PurchaseInvoicePreviewDialog: React.FC<PurchaseInvoicePreviewDialogProps> = ({
  open,
  onClose,
  invoice,
  onEdit
}) => {
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null);

  useEffect(() => {
    if (open) {
      loadCompanyInfo();
    }
  }, [open, invoice]);

  const loadCompanyInfo = async () => {
    try {
      const info = await companyInfoService.getCompanyInfo();
      setCompanyInfo(info);
    } catch (error) {
      console.error('Failed to load company info:', error);
    }
  };

  if (!invoice) return null;

  const getStatusLabel = (status?: string) => {
    switch (status) {
      case 'Draft': return isRTL ? 'مسودة' : 'Draft';
      case 'Posted': return isRTL ? 'مرحّل' : 'Posted';
      default: return status || '-';
    }
  };

  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'Draft': return '#9e9e9e';
      case 'Posted': return '#4caf50';
      default: return '#9e9e9e';
    }
  };

  const getStatusChipColor = (status?: string) => {
    switch (status) {
      case 'Draft': return 'default';
      case 'Posted': return 'success';
      default: return 'default';
    }
  };

  const getPaymentStatusLabel = (status?: string) => {
    switch (status) {
      case 'Unpaid': return isRTL ? 'غير مدفوع' : 'Unpaid';
      case 'PartiallyPaid': return isRTL ? 'مدفوع جزئياً' : 'Partially Paid';
      case 'Paid': return isRTL ? 'مدفوع' : 'Paid';
      default: return status || '-';
    }
  };

  const getPaymentStatusColor = (status?: string) => {
    switch (status) {
      case 'Unpaid': return '#f44336';
      case 'PartiallyPaid': return '#ff9800';
      case 'Paid': return '#4caf50';
      default: return '#9e9e9e';
    }
  };

  const getPaymentStatusChipColor = (status?: string) => {
    switch (status) {
      case 'Unpaid': return 'error';
      case 'PartiallyPaid': return 'warning';
      case 'Paid': return 'success';
      default: return 'default';
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString(isRTL ? 'ar-KW' : 'en-GB');
  };

  const formatCurrency = (amount?: number) => {
    if (amount === undefined || amount === null) return '0.000';
    return amount.toFixed(3);
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank', 'width=900,height=700');
    if (!printWindow) return;

    const dir = isRTL ? 'rtl' : 'ltr';
    const align = isRTL ? 'right' : 'left';
    const alignOpp = isRTL ? 'left' : 'right';
    const currency = isRTL ? 'د.ك' : 'KWD';
    const themeColor = '#f44336';

    let itemsRows = '';
    invoice.details.forEach((detail, idx) => {
      const lineAmount = detail.quantity * detail.unitPrice;
      const lineDiscount = lineAmount * ((detail.discountPercent ?? 0) / 100);
      const lineAfterDiscount = lineAmount - lineDiscount;
      const lineTax = lineAfterDiscount * ((detail.taxPercent ?? 0) / 100);
      const lineTotal = lineAfterDiscount + lineTax;

      itemsRows += `
        <tr>
          <td style="text-align:center;">${idx + 1}</td>
          <td>
            <strong>${detail.itemCode}</strong><br/>
            <span style="color:#666;font-size:11px;">${isRTL ? detail.itemNameAr : detail.itemName}</span>
            ${detail.batchNumber ? `<br/><span style="color:#ff9800;font-size:10px;">${isRTL ? 'باتش: ' : 'Batch: '}${detail.batchNumber}</span>` : ''}
            ${detail.expiryDate ? `<br/><span style="color:#f44336;font-size:10px;">${isRTL ? 'انتهاء: ' : 'Expiry: '}${formatDate(detail.expiryDate)}</span>` : ''}
          </td>
          <td style="text-align:center;">${isRTL ? detail.unitNameAr : detail.unitName}</td>
          <td style="text-align:center;font-weight:bold;">${formatCurrency(detail.quantity)}</td>
          <td style="text-align:center;font-weight:bold;">${formatCurrency(detail.unitPrice)}</td>
          <td style="text-align:center;">${detail.discountPercent ?? 0}%</td>
          <td style="text-align:center;">${detail.taxPercent ?? 0}%</td>
          <td style="text-align:center;font-weight:bold;color:#c62828;">${formatCurrency(lineTotal)}</td>
        </tr>
      `;
    });

    let totalsRows = `
      <tr style="background:#ffebee;">
        <td colspan="7" style="text-align:${alignOpp};font-weight:bold;padding:8px;">${isRTL ? 'المجموع الفرعي:' : 'Subtotal:'}</td>
        <td style="text-align:center;font-weight:bold;padding:8px;">${formatCurrency(invoice.subTotal)} ${currency}</td>
      </tr>
    `;

    if ((invoice.discountAmount ?? 0) > 0) {
      totalsRows += `
        <tr>
          <td colspan="7" style="text-align:${alignOpp};font-weight:bold;padding:6px;color:#ff9800;">${isRTL ? `الخصم (${invoice.discountPercent}%):` : `Discount (${invoice.discountPercent}%):`}</td>
          <td style="text-align:center;font-weight:bold;padding:6px;color:#ff9800;">-${formatCurrency(invoice.discountAmount)} ${currency}</td>
        </tr>
      `;
    }

    if ((invoice.taxAmount ?? 0) > 0) {
      totalsRows += `
        <tr>
          <td colspan="7" style="text-align:${alignOpp};font-weight:bold;padding:6px;color:#2196f3;">${isRTL ? `الضريبة (${invoice.taxPercent}%):` : `Tax (${invoice.taxPercent}%):`}</td>
          <td style="text-align:center;font-weight:bold;padding:6px;color:#2196f3;">+${formatCurrency(invoice.taxAmount)} ${currency}</td>
        </tr>
      `;
    }

    if ((invoice.shippingCost ?? 0) > 0) {
      totalsRows += `
        <tr>
          <td colspan="7" style="text-align:${alignOpp};font-weight:bold;padding:6px;color:#9c27b0;">${isRTL ? 'تكلفة الشحن:' : 'Shipping Cost:'}</td>
          <td style="text-align:center;font-weight:bold;padding:6px;color:#9c27b0;">+${formatCurrency(invoice.shippingCost)} ${currency}</td>
        </tr>
      `;
    }

    totalsRows += `
      <tr style="background:#c62828;color:white;">
        <td colspan="7" style="text-align:${alignOpp};font-weight:bold;padding:10px;font-size:14px;">${isRTL ? 'الإجمالي الكلي:' : 'Total Amount:'}</td>
        <td style="text-align:center;font-weight:bold;padding:10px;font-size:16px;">${formatCurrency(invoice.totalAmount)} ${currency}</td>
      </tr>
    `;

    if ((invoice.paidAmount ?? 0) > 0) {
      totalsRows += `
        <tr>
          <td colspan="7" style="text-align:${alignOpp};font-weight:bold;padding:6px;color:#4caf50;">${isRTL ? 'المبلغ المدفوع:' : 'Paid Amount:'}</td>
          <td style="text-align:center;font-weight:bold;padding:6px;color:#4caf50;">${formatCurrency(invoice.paidAmount)} ${currency}</td>
        </tr>
      `;
    }

    if ((invoice.remainingAmount ?? 0) > 0) {
      totalsRows += `
        <tr style="background:#fff3e0;">
          <td colspan="7" style="text-align:${alignOpp};font-weight:bold;padding:8px;color:#e65100;">${isRTL ? 'المبلغ المتبقي:' : 'Remaining Amount:'}</td>
          <td style="text-align:center;font-weight:bold;padding:8px;font-size:15px;color:#c62828;">${formatCurrency(invoice.remainingAmount)} ${currency}</td>
        </tr>
      `;
    }

    printWindow.document.write(`
      <html dir="${dir}">
      <head>
        <title>${isRTL ? 'فاتورة مشتريات' : 'Purchase Invoice'} - ${invoice.invoiceNumber}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: 'Segoe UI', Tahoma, Arial, sans-serif; padding: 20px; direction: ${dir}; color: #333; font-size: 13px; }
          .header { text-align: center; border-bottom: 3px solid ${themeColor}; padding-bottom: 15px; margin-bottom: 15px; }
          .header img { max-height: 60px; margin-bottom: 8px; }
          .header h2 { color: ${themeColor}; margin: 5px 0; font-size: 20px; }
          .header .subtitle { font-size: 11px; color: #666; }
          .doc-title { text-align: center; margin-bottom: 20px; }
          .doc-title h3 { color: ${themeColor}; font-size: 24px; border: 2px solid ${themeColor}; display: inline-block; padding: 8px 30px; border-radius: 8px; }
          .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 15px; border: 1px solid #ddd; border-radius: 8px; padding: 15px; }
          .info-item label { font-size: 11px; color: #888; display: block; margin-bottom: 2px; }
          .info-item .value { font-size: 14px; font-weight: bold; }
          .info-item .value.primary { color: #c62828; font-size: 16px; }
          .status-badge { display: inline-block; padding: 3px 12px; border-radius: 12px; font-size: 12px; font-weight: bold; color: white; }
          .supplier-box { background: #ffebee; border: 1px solid #ef9a9a; border-radius: 8px; padding: 12px; margin-bottom: 10px; }
          .supplier-box label { font-size: 11px; color: #666; }
          .supplier-box .name { font-size: 16px; font-weight: bold; color: #c62828; }
          .supplier-box .code { font-size: 11px; color: #888; }
          .warehouse-box { background: #e8f5e9; border: 1px solid #a5d6a7; border-radius: 8px; padding: 12px; margin-bottom: 10px; }
          .warehouse-box label { font-size: 11px; color: #666; }
          .warehouse-box .name { font-size: 14px; font-weight: bold; color: #2e7d32; }
          .warehouse-box .code { font-size: 11px; color: #888; }
          .notes-box { background: #fff3e0; border: 1px solid #ffe0b2; border-radius: 8px; padding: 10px; margin-bottom: 15px; }
          .notes-box label { font-size: 11px; color: #888; font-weight: bold; }
          .section-title { background: ${themeColor}; color: white; padding: 10px 15px; font-weight: bold; font-size: 14px; border-radius: 8px 8px 0 0; }
          table { width: 100%; border-collapse: collapse; }
          thead th { background: #ffebee; color: #c62828; padding: 10px 6px; font-size: 11px; text-align: ${align}; border-bottom: 2px solid #ef9a9a; }
          thead th.center { text-align: center; }
          tbody td { padding: 7px 6px; border-bottom: 1px solid #eee; font-size: 12px; }
          tbody tr:nth-child(even) { background: #fafafa; }
          .table-wrapper { border: 1px solid #ddd; border-radius: 0 0 8px 8px; overflow: hidden; margin-bottom: 20px; }
          .signatures { display: flex; justify-content: space-between; margin-top: 60px; }
          .sig-block { text-align: center; width: 30%; }
          .sig-line { border-top: 1px solid #333; padding-top: 8px; margin-top: 60px; font-weight: bold; font-size: 12px; }
          .print-date { text-align: ${alignOpp}; font-size: 10px; color: #999; margin-bottom: 10px; }
          @media print { @page { size: A4; margin: 10mm; } }
        </style>
      </head>
      <body>
        <div class="print-date">${isRTL ? 'تاريخ الطباعة:' : 'Print Date:'} ${new Date().toLocaleString(isRTL ? 'ar-KW' : 'en-GB')}</div>

        ${companyInfo ? `
        <div class="header">
          ${companyInfo.logoPath ? `<img src="${config.API_ORIGIN}${companyInfo.logoPath}" onerror="this.style.display='none'" />` : ''}
          <h2>${isRTL ? companyInfo.companyNameAr : companyInfo.companyName}</h2>
          <div class="subtitle">
            ${companyInfo.address ? companyInfo.address + ' | ' : ''}${companyInfo.phone ? companyInfo.phone + ' | ' : ''}${companyInfo.email || ''}${companyInfo.website ? ' | ' + companyInfo.website : ''}
          </div>
        </div>
        ` : ''}

        <div class="doc-title">
          <h3>${isRTL ? 'فاتورة مشتريات' : 'Purchase Invoice'}</h3>
        </div>

        <div class="info-grid">
          <div class="info-item">
            <label>${isRTL ? 'رقم الفاتورة' : 'Invoice Number'}</label>
            <div class="value primary">${invoice.invoiceNumber}</div>
          </div>
          ${invoice.supplierInvoiceNumber ? `
          <div class="info-item">
            <label>${isRTL ? 'رقم فاتورة المورد' : 'Supplier Invoice No.'}</label>
            <div class="value">${invoice.supplierInvoiceNumber}</div>
          </div>
          ` : ''}
          <div class="info-item">
            <label>${isRTL ? 'التاريخ' : 'Date'}</label>
            <div class="value">${formatDate(invoice.invoiceDate)}</div>
          </div>
          <div class="info-item">
            <label>${isRTL ? 'حالة الفاتورة' : 'Invoice Status'}</label>
            <div><span class="status-badge" style="background:${getStatusColor(invoice.status)}">${getStatusLabel(invoice.status)}</span></div>
          </div>
          <div class="info-item">
            <label>${isRTL ? 'حالة الدفع' : 'Payment Status'}</label>
            <div><span class="status-badge" style="background:${getPaymentStatusColor(invoice.paymentStatus)}">${getPaymentStatusLabel(invoice.paymentStatus)}</span></div>
          </div>
          ${invoice.journalEntryId ? `
          <div class="info-item">
            <label>${isRTL ? 'رقم القيد' : 'Journal Entry'}</label>
            <div class="value" style="color:#4caf50;">${invoice.journalEntryNumber || ''} (#${invoice.journalEntryId})</div>
          </div>
          ` : ''}
          ${invoice.purchaseOrderId ? `
          <div class="info-item">
            <label>${isRTL ? 'رقم طلب الشراء' : 'PO Number'}</label>
            <div class="value" style="color:#2196f3;">#${invoice.purchaseOrderId}</div>
          </div>
          ` : ''}
        </div>

        <div class="supplier-box">
          <label>${isRTL ? 'المورد' : 'Supplier'}</label>
          <div class="name">${isRTL ? invoice.supplierNameAr : invoice.supplierName}</div>
          <div class="code">${invoice.supplierCode}</div>
        </div>

        <div class="warehouse-box">
          <label>${isRTL ? 'المخزن' : 'Warehouse'}</label>
          <div class="name">${isRTL ? invoice.warehouseNameAr : invoice.warehouseName}</div>
          <div class="code">${invoice.warehouseCode}</div>
        </div>

        ${invoice.notes ? `
        <div class="notes-box">
          <label>${isRTL ? 'ملاحظات:' : 'Notes:'}</label>
          <div>${invoice.notes}</div>
        </div>
        ` : ''}

        <div class="section-title">📦 ${isRTL ? 'تفاصيل الأصناف' : 'Items Details'}</div>
        <div class="table-wrapper">
          <table>
            <thead>
              <tr>
                <th class="center">#</th>
                <th>${isRTL ? 'الصنف' : 'Item'}</th>
                <th class="center">${isRTL ? 'الوحدة' : 'Unit'}</th>
                <th class="center">${isRTL ? 'الكمية' : 'Qty'}</th>
                <th class="center">${isRTL ? 'السعر' : 'Price'}</th>
                <th class="center">${isRTL ? 'الخصم%' : 'Disc%'}</th>
                <th class="center">${isRTL ? 'الضريبة%' : 'Tax%'}</th>
                <th class="center">${isRTL ? 'الإجمالي' : 'Total'}</th>
              </tr>
            </thead>
            <tbody>
              ${itemsRows}
              ${totalsRows}
            </tbody>
          </table>
        </div>

        <div class="signatures">
          <div class="sig-block"><div class="sig-line">${isRTL ? 'المعتمد' : 'Approved By'}</div></div>
          <div class="sig-block"><div class="sig-line">${isRTL ? 'أمين المخزن' : 'Warehouse Keeper'}</div></div>
          <div class="sig-block"><div class="sig-line">${isRTL ? 'المحاسب' : 'Accountant'}</div></div>
        </div>
      </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      // printWindow stays open for user review
    }, 500);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      dir={isRTL ? 'rtl' : 'ltr'}
      PaperProps={{
        sx: {
          borderRadius: 3,
          bgcolor: isDarkMode ? '#1a1a2e' : 'white'
        }
      }}
    >
      <DialogTitle
        sx={{
          background: isDarkMode
            ? 'linear-gradient(135deg, #c62828 0%, #e53935 100%)'
            : 'linear-gradient(135deg, #f44336 0%, #ef5350 100%)',
          color: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          py: 2
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ReceiptIcon />
          <Box component="span">
            {isRTL ? 'معاينة فاتورة مشتريات' : 'Purchase Invoice Preview'}
          </Box>
        </Box>
        <IconButton onClick={onClose} sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ mt: 3 }}>
        {/* Header Info */}
        <Paper elevation={2} sx={{ p: 3, mb: 3, bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : '#f5f5f5', borderRadius: 2 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>{isRTL ? 'رقم الفاتورة' : 'Invoice Number'}</Typography>
              <Typography variant="h6" fontWeight="bold" color="error">{invoice.invoiceNumber}</Typography>
            </Box>
            {invoice.supplierInvoiceNumber && (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>{isRTL ? 'رقم فاتورة المورد' : 'Supplier Invoice No.'}</Typography>
                <Typography variant="body1" component="div" fontWeight="bold">{invoice.supplierInvoiceNumber}</Typography>
              </Box>
            )}
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>{isRTL ? 'التاريخ' : 'Date'}</Typography>
              <Typography variant="body1" component="div" fontWeight="bold">{formatDate(invoice.invoiceDate)}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>{isRTL ? 'حالة الفاتورة' : 'Invoice Status'}</Typography>
              <Box><Chip label={getStatusLabel(invoice.status)} color={getStatusChipColor(invoice.status)} size="small" sx={{ fontWeight: 'bold' }} /></Box>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>{isRTL ? 'حالة الدفع' : 'Payment Status'}</Typography>
              <Box><Chip label={getPaymentStatusLabel(invoice.paymentStatus)} color={getPaymentStatusChipColor(invoice.paymentStatus)} size="small" sx={{ fontWeight: 'bold' }} /></Box>
            </Box>
            {invoice.journalEntryId && (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>{isRTL ? 'رقم القيد' : 'Journal Entry'}</Typography>
                <Typography variant="body1" fontWeight="bold" color="success.main">{invoice.journalEntryNumber || ''} (#{invoice.journalEntryId})</Typography>
              </Box>
            )}
            {invoice.purchaseOrderId && (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>{isRTL ? 'رقم طلب الشراء' : 'PO Number'}</Typography>
                <Typography variant="body1" fontWeight="bold" color="info.main">#{invoice.purchaseOrderId}</Typography>
              </Box>
            )}
          </Box>

          <Divider sx={{ my: 2 }} />

          <Box sx={{ p: 2, bgcolor: isDarkMode ? 'rgba(244,67,54,0.1)' : '#ffebee', borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>{isRTL ? 'المورد' : 'Supplier'}</Typography>
            <Typography variant="h6" fontWeight="bold" color="error.main">{isRTL ? invoice.supplierNameAr : invoice.supplierName}</Typography>
            <Typography variant="caption" color="text.secondary">{invoice.supplierCode}</Typography>
          </Box>

          <Box sx={{ mt: 2, p: 2, bgcolor: isDarkMode ? 'rgba(76,175,80,0.1)' : '#e8f5e9', borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>{isRTL ? 'المخزن' : 'Warehouse'}</Typography>
            <Typography variant="body1" fontWeight="bold" color="success.main">{isRTL ? invoice.warehouseNameAr : invoice.warehouseName}</Typography>
            <Typography variant="caption" color="text.secondary">{invoice.warehouseCode}</Typography>
          </Box>

          {invoice.notes && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="text.secondary" fontWeight="bold" sx={{ fontSize: '0.75rem' }}>{isRTL ? 'ملاحظات:' : 'Notes:'}</Typography>
              <Typography variant="body2">{invoice.notes}</Typography>
            </Box>
          )}
        </Paper>

        {/* Items Details */}
        <Paper elevation={2} sx={{ mb: 2, borderRadius: 2, overflow: 'hidden' }}>
          <Box sx={{ bgcolor: isDarkMode ? 'rgba(244,67,54,0.3)' : '#f44336', color: 'white', p: 1.5, fontWeight: 'bold' }}>
            {isRTL ? '📦 تفاصيل الأصناف' : '📦 Items Details'}
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>{isRTL ? 'الصنف' : 'Item'}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>{isRTL ? 'الوحدة' : 'Unit'}</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>{isRTL ? 'الكمية' : 'Qty'}</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>{isRTL ? 'السعر' : 'Price'}</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>{isRTL ? 'الخصم%' : 'Disc%'}</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>{isRTL ? 'الضريبة%' : 'Tax%'}</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>{isRTL ? 'الإجمالي' : 'Total'}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {invoice.details.map((detail, idx) => {
                  const lineAmount = detail.quantity * detail.unitPrice;
                  const lineDiscount = lineAmount * ((detail.discountPercent ?? 0) / 100);
                  const lineAfterDiscount = lineAmount - lineDiscount;
                  const lineTax = lineAfterDiscount * ((detail.taxPercent ?? 0) / 100);
                  const lineTotal = lineAfterDiscount + lineTax;
                  return (
                    <TableRow key={idx} hover>
                      <TableCell sx={{ fontSize: '0.8rem' }}>{idx + 1}</TableCell>
                      <TableCell sx={{ fontSize: '0.8rem' }}>
                        <strong>{detail.itemCode}</strong><br />
                        <Typography variant="caption" color="text.secondary">{isRTL ? detail.itemNameAr : detail.itemName}</Typography>
                        {detail.batchNumber && (<><br /><Typography variant="caption" color="warning.main">{isRTL ? 'باتش: ' : 'Batch: '}{detail.batchNumber}</Typography></>)}
                        {detail.expiryDate && (<><br /><Typography variant="caption" color="error.main">{isRTL ? 'انتهاء: ' : 'Expiry: '}{formatDate(detail.expiryDate)}</Typography></>)}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.8rem' }}>{isRTL ? detail.unitNameAr : detail.unitName}</TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.85rem', fontWeight: 'bold' }}>{formatCurrency(detail.quantity)}</TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.85rem', fontWeight: 'bold' }}>{formatCurrency(detail.unitPrice)}</TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.8rem' }}>{detail.discountPercent ?? 0}%</TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.8rem' }}>{detail.taxPercent ?? 0}%</TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.85rem', fontWeight: 'bold', color: 'error.main' }}>{formatCurrency(lineTotal)}</TableCell>
                    </TableRow>
                  );
                })}
                
                {/* Subtotal */}
                <TableRow>
                  <TableCell colSpan={7} align="right" sx={{ fontWeight: 'bold', fontSize: '0.85rem' }}>{isRTL ? 'المجموع الفرعي:' : 'Subtotal:'}</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.9rem' }}>{formatCurrency(invoice.subTotal)} {isRTL ? 'د.ك' : 'KWD'}</TableCell>
                </TableRow>
                {(invoice.discountAmount ?? 0) > 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="right" sx={{ fontWeight: 'bold', fontSize: '0.85rem' }}>{isRTL ? `الخصم (${invoice.discountPercent}%):` : `Discount (${invoice.discountPercent}%):`}</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.9rem', color: 'warning.main' }}>-{formatCurrency(invoice.discountAmount)} {isRTL ? 'د.ك' : 'KWD'}</TableCell>
                  </TableRow>
                )}
                {(invoice.taxAmount ?? 0) > 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="right" sx={{ fontWeight: 'bold', fontSize: '0.85rem' }}>{isRTL ? `الضريبة (${invoice.taxPercent}%):` : `Tax (${invoice.taxPercent}%):`}</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.9rem', color: 'info.main' }}>+{formatCurrency(invoice.taxAmount)} {isRTL ? 'د.ك' : 'KWD'}</TableCell>
                  </TableRow>
                )}
                {(invoice.shippingCost ?? 0) > 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="right" sx={{ fontWeight: 'bold', fontSize: '0.85rem' }}>{isRTL ? 'تكلفة الشحن:' : 'Shipping Cost:'}</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.9rem', color: 'secondary.main' }}>+{formatCurrency(invoice.shippingCost)} {isRTL ? 'د.ك' : 'KWD'}</TableCell>
                  </TableRow>
                )}
                <TableRow>
                  <TableCell colSpan={7} align="right" sx={{ fontWeight: 'bold', fontSize: '1rem', bgcolor: isDarkMode ? 'rgba(244,67,54,0.2)' : 'rgba(244,67,54,0.1)' }}>{isRTL ? 'الإجمالي الكلي:' : 'Total Amount:'}</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '1.1rem', color: 'success.main', bgcolor: isDarkMode ? 'rgba(244,67,54,0.2)' : 'rgba(244,67,54,0.1)' }}>{formatCurrency(invoice.totalAmount)} {isRTL ? 'د.ك' : 'KWD'}</TableCell>
                </TableRow>
                {(invoice.paidAmount ?? 0) > 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="right" sx={{ fontWeight: 'bold', fontSize: '0.85rem' }}>{isRTL ? 'المبلغ المدفوع:' : 'Paid Amount:'}</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.9rem', color: 'success.main' }}>{formatCurrency(invoice.paidAmount)} {isRTL ? 'د.ك' : 'KWD'}</TableCell>
                  </TableRow>
                )}
                {(invoice.remainingAmount ?? 0) > 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="right" sx={{ fontWeight: 'bold', fontSize: '0.9rem', bgcolor: isDarkMode ? 'rgba(255,152,0,0.2)' : 'rgba(255,152,0,0.1)' }}>{isRTL ? 'المبلغ المتبقي:' : 'Remaining Amount:'}</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '1rem', color: 'error.main', bgcolor: isDarkMode ? 'rgba(255,152,0,0.2)' : 'rgba(255,152,0,0.1)' }}>{formatCurrency(invoice.remainingAmount)} {isRTL ? 'د.ك' : 'KWD'}</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </DialogContent>

      <DialogActions sx={{ p: 3, bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : '#f5f5f5' }}>
        <Button onClick={onClose} size="large">{isRTL ? 'إغلاق' : 'Close'}</Button>
        {invoice?.status === 'Draft' && onEdit && (
          <Button variant="outlined" startIcon={<EditIcon />} onClick={() => { onEdit(); onClose(); }} size="large" color="error">{isRTL ? 'تعديل' : 'Edit'}</Button>
        )}
        <Button
          variant="contained"
          startIcon={<PrintIcon />}
          onClick={handlePrint}
          size="large"
          sx={{ background: isDarkMode ? 'linear-gradient(45deg, #c62828 30%, #e53935 90%)' : 'linear-gradient(45deg, #f44336 30%, #ef5350 90%)' }}
        >
          {isRTL ? 'طباعة' : 'Print'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PurchaseInvoicePreviewDialog;